import React, { Suspense, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch, Redirect } from 'react-router-dom'
import { theme } from 'theme'
import { createGetRequestsAreLoading } from '../state'
import { STATE_KEYS, setLoading } from '../state/requests'
import {
    getProfileDetail,
    selectIsCampaignsEmpty,
    selectProfileProperties,
    selectUserIsRegistered,
} from 'state/profile'
import { useLocation } from 'react-router-dom'
import { ConstantsActions, getOnboardingOptions } from '../state/constants'
import { getToken } from '../services/auth'
import styled from 'particles/themes/styled'
import PrivateRoute from './PrivateRoute'
import SmallScreenNavBar from './NavBar/SmallScreenNavBar'
import Navbar from './NavBar'
import GeneralLoading from './GeneralLoading'
import Tooltips from './Tooltips'
import { useIsLoginRoute } from 'hooks'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import PermissionProvider from 'components/Shared/Permission/PermissionProvider'
import { identifyEvent } from 'config/analytics'
import { pushUserId } from 'config/gtm'
import { useRedirect } from 'hooks'

// lazy load components
const Campaign = React.lazy(() => import('./Campaign'))
const Auth = React.lazy(() => import('./Auth'))
const AuthCallback = React.lazy(() => import('./Auth/Callback'))
const PermissionDenied = React.lazy(() =>
    import('./Shared/ErrorPage').then(module => ({ default: module.PermissionDenied })),
)
const PageNotFound = React.lazy(() => import('./Shared/ErrorPage').then(module => ({ default: module.PageNotFound })))
const RoutingHandler = React.lazy(() => import('./RoutingHandler'))
const AmbassadorsDashboard = React.lazy(() => import('./Ambassadors'))
const Submissions = React.lazy(() => import('./Submissions'))
const TeamsList = React.lazy(() => import('./Team/Teams/TeamsList'))
const Roles = React.lazy(() => import('./Team/Teams/Roles'))
const BusinessDetails = React.lazy(() => import('./Team/BusinessDetails'))
const ToastContainer = React.lazy(() =>
    import('particles/Alert/style').then(module => ({ default: module.ToastContainer })),
)
const SnackbarContainer = React.lazy(() =>
    import('particles/Alert/style').then(module => ({ default: module.SnackbarContainer })),
)
const DraggableBriefModal = React.lazy(() =>
    import('./Submissions/Review/common/BriefRequirements').then(module => ({ default: module.DraggableBriefModal })),
)
const Team = React.lazy(() => import('./Team'))
const Banners = React.lazy(() => import('./Banners'))
const CampaignsList = React.lazy(() => import('./CampaignList'))
const GuidedTemplateSettings = React.lazy(() => import('./CampaignList/GuidedTemplateSettings'))
const ZendeskWebWidget = React.lazy(() => import('./ZendeskWebWidget'))

export enum ROUTES {
    CampaignsDashboard = '/campaigns',
    SubmissionsDashboard = '/submissions',
    AmbassadorsDashboard = '/ambassadors',
    TeamDashboard = '/teams',
    RoleDashboard = '/teams/:uuid/roles',
    BusinessInformation = '/teams/:uuid/business-details',
    Team = '/team',
    Campaign = '/campaign',
    Banners = '/banners',
}

const authRoutes = {
    Login: '/login',
    LoginExpired: '/login/expired',
    ForgotPassword: '/forgot-password',
    SignUp: '/sign-up',
    ResetPassword: '/reset-password',
    Register: '/register',
    AuthCallback: '/auth/callback',
} as const

declare global {
    interface Window {
        zE: (widget: string, command: string, ...args: any[]) => (() => void) | void
    }
}
const Container = styled.div<{ isLoginRoute: boolean }>`
    background-color: ${theme.newColors.neutral100};
    min-height: calc(100vh - env(safe-area-inset-bottom));
    display: flex;
    position: fixed; // adding fix position in the root to handle mobile keyboard behaviour that shift the height position
    top: 0;
    left: 0;
    width: 100vw;
    @media screen and (max-width: 768px) {
        min-height: -webkit-fill-available;
        max-height: -webkit-fill-available;
        height: calc(100vh - env(safe-area-inset-bottom) - env(safe-area-inset-top));

        #content {
            ${({ isLoginRoute }) => !isLoginRoute && 'padding-top: 60px;'}
        }
    }
    #content {
        max-height: 100vh;
        overflow-y: auto;
        width: 100%;
    }
    #small-navbar {
        width: 100%;
        position: fixed;
        height: fit-content;
        top: 0;
        background-color: ${theme.newColors.neutral100};
        z-index: 110;
        padding: 16px;
    }

    @keyframes snack-bar-fade-out {
        0% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }

    .snack-bar-fade-out {
        animation: snack-bar-fade-out 0.3s ease-out;
    }

    @keyframes snack-bar-fade-in {
        0% {
            opacity: 0;
        }
        100% {
            transform: rotate(0) scale(1);
            opacity: 1;
        }
    }

    .snack-bar-fade-in {
        animation: snack-bar-fade-in 0.3s ease-in;
    }
`
const Root: React.FC = () => {
    const dispatch = useDispatch()
    const token = getToken()
    const location = useLocation()
    const getIsLoading = createGetRequestsAreLoading([STATE_KEYS.Profile, STATE_KEYS.Constants])
    const isLoading = useSelector(getIsLoading)
    const isLoginRoute = useIsLoginRoute()
    const profileProperties = useSelector(selectProfileProperties)
    const isRegistered = useSelector(selectUserIsRegistered)
    const isShowNavBar = Boolean(!isLoginRoute && token && isRegistered)
    const { redirect } = useRedirect()
    const isCampaignsEmpty = useSelector(selectIsCampaignsEmpty)
    const [showSlowMessage, setShowSlowMessage] = useState(false)

    useEffect(() => {
        setShowSlowMessage(false)
        const timer = setTimeout(() => setShowSlowMessage(true), 3000)
        return () => clearTimeout(timer)
    }, [isLoading])

    useEffect(() => {
        if (token !== null) {
            dispatch(getProfileDetail())
        } else {
            dispatch(setLoading({ stateKey: STATE_KEYS.Profile, isLoading: false }))
        }
        dispatch({ type: ConstantsActions.CONSTANTS_REQUEST })
        dispatch(getOnboardingOptions())
    }, [dispatch, token, setLoading])

    useEffect(() => {
        localStorage.removeItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
    }, [])

    useEffect(() => {
        const exceptionPaths: string[] = [...Object.values(authRoutes).flat(), '/404', '/403']
        if (profileProperties.clientID) {
            identifyEvent(profileProperties)
            pushUserId(profileProperties.clientID)
        }
        if (!isRegistered && profileProperties.clientID && !exceptionPaths.includes(location.pathname)) {
            redirect(`/register?member=${profileProperties.clientID}`)
        }
    }, [profileProperties.clientID, isRegistered, location.pathname])

    if (isLoading) return <GeneralLoading showSlowMessage={showSlowMessage} />

    return (
        <PermissionProvider>
            <Suspense fallback={<GeneralLoading showSlowMessage={showSlowMessage} />}>
                <Container isLoginRoute={isLoginRoute}>
                    {isShowNavBar && <Navbar />}
                    {isShowNavBar && <SmallScreenNavBar />}
                    <div id="content">
                        <Switch>
                            <Route
                                path={[
                                    authRoutes.ForgotPassword,
                                    authRoutes.SignUp,
                                    authRoutes.ResetPassword,
                                    authRoutes.Register,
                                    authRoutes.Login,
                                ]}
                                exact
                                component={Auth}
                            />
                            <Route path={authRoutes.AuthCallback} exact={true} component={AuthCallback} />
                            <Route path="/403" exact={true} component={PermissionDenied} />
                            <PrivateRoute exact path="/routing" component={RoutingHandler} />
                            <PrivateRoute
                                path={ROUTES.AmbassadorsDashboard}
                                exact={true}
                                component={AmbassadorsDashboard}
                            />
                            <PrivateRoute path={ROUTES.CampaignsDashboard} exact={true} component={CampaignsList} />
                            <PrivateRoute path={ROUTES.SubmissionsDashboard} component={Submissions} />
                            <PrivateRoute path={ROUTES.Banners} component={Banners} />
                            <PrivateRoute path={ROUTES.Campaign} component={Campaign} />
                            <PrivateRoute path={ROUTES.TeamDashboard} exact component={TeamsList} />
                            <PrivateRoute path={ROUTES.RoleDashboard} exact component={Roles} />
                            <PrivateRoute path={ROUTES.BusinessInformation} exact component={BusinessDetails} />
                            <PrivateRoute path={ROUTES.Team} component={Team} />
                            <PrivateRoute
                                path="/"
                                exact={true}
                                render={() => (
                                    <Redirect
                                        to={isCampaignsEmpty ? ROUTES.CampaignsDashboard : ROUTES.SubmissionsDashboard}
                                    />
                                )}
                            />
                            <Route path="*" component={PageNotFound} />
                        </Switch>
                    </div>

                    <DraggableBriefModal />
                    <ToastContainer
                        containerId="toast-container"
                        limit={1}
                        position="bottom-center"
                        enableMultiContainer
                    />
                    <SnackbarContainer
                        containerId="snackbar-container"
                        limit={1}
                        position="bottom-center"
                        enableMultiContainer
                    />
                    <Tooltips />
                    <GuidedTemplateSettings />
                    {!!token && <ZendeskWebWidget />}
                </Container>
            </Suspense>
        </PermissionProvider>
    )
}

export default Root
